
/**
 * Module dependencies.
 */

import {
  NavThemeProps,
  borderOpacity,
  navTransition,
  overlayBorderOpacity
} from './nav-theme';
import { color, units } from 'src/styles/utils';
import { navElementAttrs } from './nav-element';
import { switchProp } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Export `NavbarButtonVariant` type.
 */

export type NavbarButtonVariant = 'fill' | 'outlined';

/**
 * `Props` type.
 */

type Props = NavThemeProps & {
  variant?: NavbarButtonVariant
};

/**
 * Button themes.
 *
 * The design only specifies the resting states, so the hovers reuse the
 * palette already used by `src/components/core/buttons/button.tsx`.
 */

const buttonThemes = {
  dark: switchProp('variant', {
    fill: css`
      background-color: ${color('grey400')};
      border-color: ${color('grey400')};
      color: ${color('white')};

      &:focus-visible,
      &:hover {
        background-color: ${color('grey900')};
        border-color: ${color('grey900')};
      }
    `,
    outlined: css`
      background-color: transparent;
      border-color: ${color.transparentize('neutralDarkest', borderOpacity)};
      color: ${color('black')};

      &:focus-visible,
      &:hover {
        background-color: ${color('grey400')};
        border-color: ${color('grey400')};
        color: ${color('white')};
      }
    `
  }),
  light: switchProp('variant', {
    fill: css`
      background-color: ${color('white')};
      border-color: ${color('white')};
      color: ${color('black')};

      &:focus-visible,
      &:hover {
        background-color: ${color('beige100')};
        border-color: ${color('beige100')};
      }
    `,
    outlined: css`
      background-color: transparent;
      border-color: ${color.transparentize('white', overlayBorderOpacity)};
      color: ${color('white')};

      &:focus-visible,
      &:hover {
        background-color: ${color.transparentize('white', 0.15)};
        border-color: ${color('white')};
      }
    `
  })
};

/**
 * `NavbarButton` styled component.
 *
 * The `Button` of the navbar actions: a pill that, unlike the site wide
 * button, follows the navbar color theme and has no fixed width.
 */

const NavbarButton = styled.button.attrs(navElementAttrs)<Props>`
  -webkit-tap-highlight-color: transparent;
  align-items: center;
  appearance: none;
  border: 1px solid;
  border-radius: 100px;
  cursor: pointer;
  display: inline-flex;
  font-size: 14px;
  justify-content: center;
  letter-spacing: 0.01em;
  line-height: 1.5;
  outline: none;
  padding: ${units(1.5)} ${units(3)};
  text-align: center;
  text-decoration: none;
  text-transform: uppercase;
  transition: ${navTransition};
  transition-property: background-color, border-color, color;
  white-space: nowrap;

  ${switchProp('colorTheme', buttonThemes)}
`;

/**
 * Default props.
 */

NavbarButton.defaultProps = {
  colorTheme: 'dark',
  variant: 'outlined'
};

/**
 * Export `NavbarButton` styled component.
 */

export default NavbarButton;
